import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { updateLogisticaPropiaApi } from "../../api/axios";
import { fetchLogisticaPropia, updateLogisticaEscalaField, initZonasUI, updateZonaToggle } from "../../actions/sellersActions";
import BigSpinner from "../General/BigSpinner";
import MiniSpinner from "../General/MiniSpinner";
import { toast } from "react-toastify";

export default function LogisticaPropia({ idSeller, userType }) {
  const dispatch = useDispatch();

  const isSeller   = userType.isSellerAdmin || userType.isSeller;
  const isAdminMod = userType.isAdminMod;
  const isCategory = userType.isCategory;

  // Store
  const lp        = useSelector(s => s.sellers.logisticaPropia); // objeto completo
  const escalas   = lp?.escalas || [];
  const zonasUI   = lp?.zonasUI || null; // { amba:{enabled,envioGratis}, ... }

  // UI state
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Carga inicial (usa caché si existe)
  useEffect(() => {
    if (idSeller != null) {
      dispatch(fetchLogisticaPropia(idSeller, { force: false }));
    }
  }, [idSeller, dispatch]);

  // Inicializar zonasUI a partir de escalas/envioGratisZona la PRIMERA vez
  useEffect(() => {
    if (lp && !lp.zonasUI) {
      dispatch(initZonasUI(idSeller));
    }
  }, [lp, idSeller, dispatch]);

  // Spinner de carga
  useEffect(() => {
    if (lp && escalas.length >= 0) setIsFetchingData(false);
  }, [lp, escalas.length]);

  // Zonas calculadas desde el store (sin estado local)
  const zonas = useMemo(() => ([
    { id: 1, name: "amba",      enabled: zonasUI?.amba?.enabled || false,      envioGratis: zonasUI?.amba?.envioGratis || false },
    { id: 2, name: "interior",  enabled: zonasUI?.interior?.enabled || false,  envioGratis: zonasUI?.interior?.envioGratis || false },
    { id: 3, name: "patagonia", enabled: zonasUI?.patagonia?.enabled || false, envioGratis: zonasUI?.patagonia?.envioGratis || false }
  ]), [zonasUI]);

  // Cambiar toggles de zona (Redux)
  const handleChangeZona = (e, zonaName) => {
    const { name } = e.target; // 'enabled' | 'envioGratis'
    dispatch(updateZonaToggle({ idSeller, zona: zonaName, field: name }));
  };

  // Cambiar costo / promesa en una escala (Redux)
  const handleChangeEscala = (e, idEscala, zonaName) => {
    const { name, value } = e.target; // name = 'costo' | 'diasEntrega'
    const max = Number(e.target.getAttribute('maxLength') || 0);
    if (max && String(value).length > max) return;

    dispatch(updateLogisticaEscalaField({
      idSeller, idEscala, zona: zonaName, field: name, value
    }));
  };

  // Guardar cambios
  const onSubmit = (e) => {
    e.preventDefault();
    setIsUpdating(true);

    const updatedZonas = zonas.map((zona) => {
      const updatedEscalas = [];

      if (zona.enabled) {
        for (let j = 0; j < escalas.length; j++) {
          const logistica = escalas[j];
          const costo       = logistica[zona.name]?.costo ?? null;
          const diasEntrega = logistica[zona.name]?.diasEntrega ?? null;

          if (diasEntrega && !costo) {
            toast.warn("Falta completar un costo");
            setIsUpdating(false);
            return null;
          }
          if (costo && !diasEntrega) {
            toast.warn("Falta completar una promesa de entrega");
            setIsUpdating(false);
            return null;
          }

          if (costo != null || diasEntrega != null) {
            updatedEscalas.push({
              idEscala: logistica.idEscala,
              entrega: { costo, diasEntrega }
            });
          }
        }
      }

      return {
        idZona: zona.id,
        habilitada: zona.enabled,
        envioGratis: zona.envioGratis,
        escalas: updatedEscalas
      };
    });

    // Si algún return null por validación, abortamos
    if (updatedZonas.some((z) => z === null)) return;

    updateLogisticaPropiaApi({ idSeller: Number(idSeller), zonas: updatedZonas })
      .then(() => {
        toast.success("Logística propia actualizada");
      })
      .catch((err) => {
        (err.response?.data?.errores)
          ? err.response.data.errores.forEach((error) => toast.error(error))
          : toast.error("La logística propia no pudo ser actualizada");
      })
      .finally(() => setIsUpdating(false));
  };

  return (
    <div className="row card align-items-center justify-content-center">
      {isFetchingData ? (
        <BigSpinner />
      ) : (
        <div className="animated fadeIn">
          <div className="table-responsive">
            <table className="table border-table">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: "16%" }}>
                    <small></small>
                  </th>
                  {zonas.map((zona, index) => (
                    <th key={index} colSpan={2} style={{ width: "28%" }}>
                      <small>Habilitar Log. Propia</small>
                      <span className="form-switch">
                        <input
                          name="enabled"
                          type="checkbox"
                          checked={zona.enabled}
                          className="form-check-input ms-2 mt-1"
                          onChange={(e) => handleChangeZona(e, zona.name)}
                          disabled={!isSeller}
                        />
                      </span>
                    </th>
                  ))}
                </tr>

                {(isSeller || isAdminMod || isCategory) && (
                  <tr>
                    {zonas.map((zona, index) => (
                      <th key={index} colSpan={2}>
                        <small>Envío Gratis</small>
                        <span className="form-switch">
                          <input
                            name="envioGratis"
                            type="checkbox"
                            checked={zona.envioGratis}
                            className="form-check-input ms-2 mt-1"
                            onChange={(e) => handleChangeZona(e, zona.name)}
                            disabled={!isSeller}
                          />
                        </span>
                      </th>
                    ))}
                  </tr>
                )}

                <tr>
                  <th rowSpan={2}>
                    <small className="d-block">Escala</small>
                    <small className="d-block text-muted">Kilogramo/Peso aforado</small>
                  </th>
                  <th colSpan={2}>
                    <small>Amba</small>
                  </th>
                  <th colSpan={2}>
                    <small>Interior</small>
                  </th>
                  <th colSpan={2}>
                    <small>Patagonia</small>
                  </th>
                </tr>
                <tr>
                  <th>
                    <small>Costo</small>
                  </th>
                  <th>
                    <small>Promesa Entrega</small>
                  </th>
                  <th>
                    <small>Costo</small>
                  </th>
                  <th>
                    <small>Promesa Entrega</small>
                  </th>
                  <th>
                    <small>Costo</small>
                  </th>
                  <th>
                    <small>Promesa Entrega</small>
                  </th>
                </tr>
              </thead>

              <tbody>
                {escalas.map((escala, index) => (
                  <tr key={index} className="hover-background-color">
                    <td>
                      <small>{escala?.descripcion}</small>
                    </td>

                    {zonas.map((zona, idx) => (
                      <React.Fragment key={idx}>
                        {zona.enabled ? (
                          <>
                            <td>
                              <small className="d-inline me-2">$</small>
                              <input
                                type="number"
                                name="costo"
                                value={escala[zona.name]?.costo ?? ""}
                                min="0"
                                maxLength={8}
                                className="form-control d-inline"
                                style={{ width: "100px" }}
                                onChange={(e) =>
                                  handleChangeEscala(e, escala.idEscala, zona.name)
                                }
                                disabled={!isSeller}
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                name="diasEntrega"
                                value={escala[zona.name]?.diasEntrega ?? ""}
                                min="0"
                                maxLength={3}
                                className="form-control d-inline text-center"
                                style={{ width: "60px" }}
                                onChange={(e) =>
                                  handleChangeEscala(e, escala.idEscala, zona.name)
                                }
                                disabled={!isSeller}
                              />
                              <small className="d-inline ms-2">
                                {escala[zona.name]?.diasEntrega
                                  ? (escala[zona.name]?.diasEntrega === 1 )
                                  : ""}
                              </small>
                            </td>
                          </>
                        ) : (
                          <>
                            <td></td>
                            <td></td>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="col-auto my-2">
        {isUpdating && <MiniSpinner />}
        {isSeller && (
          <button
            className="btn btn-sm background-rojo-megatone mx-2"
            onClick={onSubmit}
            disabled={isUpdating}
          >
            Actualizar Logística
          </button>
        )}
      </div>
    </div>
  );
}
